import {
  OS, BROWSER, MIN_BROWSERS, BROWSER_CHECK_STATUS
} from '@/store/modules/system';
import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';
import windowUtils from '@/utils/window-utils';

const logger = rootLogger.create('vuex-modules/g2w-webinar');

export const defaultBranding = {
  theme: {
    backgroundColor: 'eeeee7',
    borderColor: 'cccccc',
    headerColor: '114c7f'
  }
};

const mutations = {
  setWebinarInfo(state, payload) {
    state.webinarInfo = payload.webinarInfo;
  },

  setBranding(state, payload) {
    state.branding = Object.assign({}, defaultBranding, payload.branding);
  },

  reset(state) {
    state.webinarInfo = null;
    state.branding = defaultBranding;
  }
};

const actions = {
  checkBrowser: {
    root: true,
    async handler({
      state, rootState, commit, dispatch
    }) {
      // check common browser requirements (ex: minimum version)
      let browserCheckStatus = await dispatch('system/checkBrowser', {preCheck: true}, {root: true});

      if (browserCheckStatus === BROWSER_CHECK_STATUS.ok) {
        const {experienceType = ''} = state.webinarInfo || {};
        const {os, browser} = windowUtils.getParsedUserAgent();
        const osVersion = parseInt(os.version, 10);
        let supportedBrowsers;

        switch (experienceType.toLowerCase()) {
          case 'classic':
          // only Chrome
            supportedBrowsers = {[BROWSER.chrome]: MIN_BROWSERS[BROWSER.chrome]};
            commit('system/setSupportedBrowsers', {supportedBrowsers}, {root: true});

            if (browser.name !== BROWSER.chrome) {
              browserCheckStatus = BROWSER_CHECK_STATUS.notOk;
            }
            break;
          case 'broadcast':
          // no IE on Windows less than 10
            if (os.name === OS.windows && osVersion < 10) {
              supportedBrowsers = {...rootState.system.supportedBrowsers};
              delete supportedBrowsers[BROWSER.ie];
              commit('system/setSupportedBrowsers', {supportedBrowsers}, {root: true});

              if (browser.name === BROWSER.ie) {
                browserCheckStatus = BROWSER_CHECK_STATUS.notOk;
              }
            }
            break;
          case 'simulive':
          // no extra check
            break;
          default:
            logger.warn('checkBrowser - unknown experience type', {experienceType});
            break;
        }
      }

      commit('system/setBrowserCheckStatus', {browserCheckStatus}, {root: true});
      return browserCheckStatus;
    }
  },

  async getWebinarInfo({commit, rootGetters}, payload) {
    const operation = 'getWebinarInfo';
    const webinarKey = payload.webinarKey;
    logger.log(operation, {webinarKey});

    const path = `/api/webinars/${payload.webinarKey}`;
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const webinarInfo = await fetcher.fetchJSON(url, {}, {operation});
    commit('setWebinarInfo', { webinarInfo });
    return webinarInfo;
  },

  async getWebinarInfoV2({commit, rootGetters}, payload) {
    const operation = 'getWebinarInfoV2';
    const webinarKey = payload.webinarKey;
    const recurrenceKey = payload.recurrenceKey;
    logger.log(operation, {webinarKey, recurrenceKey});

    const path = `/api/V2/webinars/${webinarKey || recurrenceKey}`;
    let url = rootGetters['g2wEnv/globalUrl'](path);

    if (payload.includes) {
      url += `?includes=${payload.includes.join(',')}`;
    }

    const webinarInfo = await fetcher.fetchJSON(url, {}, {operation});
    commit('setWebinarInfo', { webinarInfo });

    if (webinarInfo.branding) {
      commit('setBranding', {branding: webinarInfo.branding});
    }

    return webinarInfo;
  },

  async getRegistrant({rootGetters}, payload) {
    const operation = 'getRegistrant';
    const {
      webinarKey, registrantKey, registrantId, email
    } = payload;
    logger.log(operation, {
      webinarKey, registrantKey, registrantId, email
    });

    let path = `/api/webinars/${webinarKey}/registrants?`;
    if (registrantKey || registrantId) {
      path += `registrantId=${registrantKey || registrantId}`;
    } else if (email) {
      path += `email=${email}`;
    }
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const registrant = await fetcher.fetchJSON(url, {}, {operation});
    return registrant;
  },

  async normalizeRegistrantKey({dispatch}, payload) {
    if (!/\d{10,}/.test(payload.registrantKey)) {
      // a registrant key should be at least 10 digits
      // resolve registrant by registrantId (9 digits) or email
      const registrant = await dispatch('getRegistrant', payload);
      return registrant.registrantKey;
    }
    return payload.registrantKey;
  },

  async getRecordings({rootGetters}, payload) {
    const operation = 'getRecordings';
    const webinarKey = payload.webinarKey;

    const path = `/api/V2/webinars/${webinarKey}/recordings?useCache=true`;
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const recordings = await fetcher.fetchJSON(url, {operation});
    return recordings;
  },

  async getBranding({commit, rootGetters}, payload) {
    const operation = 'getBranding';
    const webinarKey = payload.webinarKey;
    logger.log(operation, { webinarKey });

    const path = `/api/webinars/${webinarKey}/branding`;
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const branding = await fetcher.fetchJSON(url, {}, {operation});
    commit('setBranding', {branding});
    return branding;
  }
};

const getters = {
  experienceType(state) {
    const {experienceType = ''} = state.webinarInfo || {};
    return experienceType.toLowerCase();
  },

  isLive(state, localGetters) {
    return localGetters.isClassic || localGetters.isBroadcast;
  },

  isClassic(state, localGetters) {
    return localGetters.experienceType === 'classic';
  },

  isBroadcast(state, localGetters) {
    return localGetters.experienceType === 'broadcast';
  },

  isSimulive(state, localGetters) {
    return localGetters.experienceType === 'simulive';
  },

  brandingStyle(state) {
    return {
      borderColor: `#${state.branding.theme.borderColor}`,
      backgroundColor: `#${state.branding.theme.backgroundColor}`,
      headerColor: `#${state.branding.theme.headerColor}`
    };
  },

  allSessionsOver(state) {
    if (state.webinarInfo) {
      if (state.webinarInfo.inSession) {
        return false;
      }

      const webinarTimes = state.webinarInfo.times || state.webinarInfo.webinarTimes;
      if (webinarTimes && webinarTimes.length > 0) {
        const now = Date.now();
        return webinarTimes.every((webinarTime) => now > new Date(webinarTime.endTime).getTime());
      }
    }

    return false;
  }
};

const state = () => ({
  webinarInfo: null,
  branding: defaultBranding
});

export default {
  namespaced: true,
  mutations,
  actions,
  getters,
  state
};
